import { isPlatformBrowser } from '@angular/common';
import { Component, computed, inject, signal, OnInit, DestroyRef, PLATFORM_ID } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Media } from '@/app/core/media';
import { SeoService } from '@/app/core/seo/seo.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { catchError, of, skip } from 'rxjs';
import { CompanyCardComponent } from './company-card/company-card.component';
import { MatchedLeadsProductPublicDto } from '@/app/models/public';
import { AdSlotComponent } from '../ads/adslot/ad-slot.component';

@Component({
  selector: 'app-companies-list',
  templateUrl: './companies-list.component.html',
  imports: [
    RouterLink,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    CompanyCardComponent,
    AdSlotComponent,
  ],
})
export class CompaniesListComponent implements OnInit {
  private http       = inject(HttpClient);
  private route      = inject(ActivatedRoute);
  private router     = inject(Router);
  private seo        = inject(SeoService);
  private destroyRef = inject(DestroyRef);
  private isBrowser  = isPlatformBrowser(inject(PLATFORM_ID));
  private isDesktop  = inject(Media).match('(min-width: 1024px)');

  searchQuery = signal('');
  sortBy      = signal('random');
  companies   = signal<MatchedLeadsProductPublicDto[]>([]);
  loading     = signal(false);
  error       = signal(false);

  readonly adSlotUrl = 'https://truckdriversus.com/ad-slot-companies-top';

  /**
   * Insert a full-width ad after every N companies: 12 on mobile/tablet, 24 on desktop (≥1024px).
   * Browser only — the server can't know the viewport, and ad iframes only load client-side.
   */
  adInterval = computed(() => this.isBrowser ? (this.isDesktop() ? 24 : 12) : 0);

  showAdAfter(index: number, isLast: boolean): boolean {
    const interval = this.adInterval();
    return interval > 0 && !isLast && (index + 1) % interval === 0;
  }

  hasActiveFilters = computed(() =>
    this.searchQuery().trim().length > 0 || this.sortBy() !== 'random'
  );

  ngOnInit(): void {
    this.seo.setPage({
      title: 'Trucking Companies Hiring CDL Drivers',
      description: 'Browse trucking companies hiring CDL drivers. Compare company profiles, open jobs, pay, '
        + 'and benefits, and apply in one step on Truck Drivers USA.',
      path: '/companies',
    });

    const snapshot = this.route.snapshot.queryParamMap;
    this.searchQuery.set(snapshot.get('q')      ?? '');
    this.sortBy.set(snapshot.get('sortBy') ?? 'random');
    this.loadCompanies();

    this.route.queryParamMap
      .pipe(
        skip(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(params => {
        this.searchQuery.set(params.get('q')      ?? '');
        this.sortBy.set(params.get('sortBy') ?? 'random');
        this.loadCompanies();
      });
  }

  onSearch(): void {
    this.syncQueryParams();
  }

  onSortChange(): void {
    this.syncQueryParams();
  }

  clearFilters(): void {
    this.searchQuery.set('');
    this.sortBy.set('random');
    this.syncQueryParams();
  }

  private syncQueryParams(): void {
    const q      = this.searchQuery().trim();
    const sortBy = this.sortBy();

    this.router.navigate([], {
      relativeTo:          this.route,
      queryParams:         {
        q:      q || null,
        sortBy: sortBy !== 'random' ? sortBy : null,
      },
      queryParamsHandling: 'merge',
      replaceUrl:          true,
    });
  }

  private loadCompanies(): void {
    this.loading.set(true);
    this.error.set(false);

    let params = new HttpParams();
    const q      = this.searchQuery().trim();
    const sortBy = this.sortBy();

    if (q)                   params = params.set('q',      q);
    if (sortBy !== 'random') params = params.set('sortBy', sortBy);

    this.http.get<MatchedLeadsProductPublicDto[]>('/api/public/companies', { params })
      .pipe(
        catchError(() => {
          this.error.set(true);
          return of([]);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(companies => {
        this.companies.set(companies);
        this.loading.set(false);
      });
  }
}
